// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ExpandMoreRounded } from '@/constants/hardfin-standard-icons';
import { formatNumber } from '@/utilities/number';
import { Button, Chip } from '@mui/material';
import {
  ForwardedRef,
  MouseEventHandler,
  PropsWithChildren,
  forwardRef,
} from 'react';

interface Props {
  /**
   * A number representing the count of active options. If this value is
   * greater than zero, a chip will display the count to the right of the
   * component's children.
   */
  count: number;

  /**
   * If true, the button is disabled. Defaults to false — avoid using this to
   * reflect empty option lists. Show an in-menu empty state instead, so the
   * user can still open the menu.
   */
  isDisabled?: boolean;

  /**
   * A callback fired when the button is clicked.
   */
  onClick: MouseEventHandler<HTMLButtonElement>;
}

/**
 * Renders a button with a label and a count of active options. This function
 * also forwards a ref to the button element.
 */
function FilterMenuButtonRenderer(
  props: PropsWithChildren<Props>,
  ref: ForwardedRef<HTMLButtonElement>
) {
  return (
    <Button
      ref={ref}
      size="small"
      color="inherit"
      onClick={props.onClick}
      disabled={props.isDisabled ?? false}
      endIcon={<ExpandMoreRounded fontSize="small" />}
    >
      {props.children}
      {props.count > 0 && (
        <Chip
          size="small"
          label={formatNumber(props.count)}
          sx={{ marginLeft: 1 }}
        />
      )}
    </Button>
  );
}

/**
 * A button component that displays a label and a count of active options.
 */
export const FilterMenuButton = forwardRef(FilterMenuButtonRenderer);
